import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Thermometer, Wind, DoorOpen, Home, Gauge, Leaf, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Seo, breadcrumbSchema } from '../components/Seo';

const solutions = [
  { icon: Thermometer, title: 'Isolation thermique', desc: 'Isolation par l\'extérieur (ITE) ou par l\'intérieur (ITI) avec des matériaux performants, y compris biosourcés (fibre de bois, chanvre, liège).' },
  { icon: Wind, title: 'Ventilation (VMC)', desc: 'Installation et rénovation de VMC pour renouveler l\'air, limiter l\'humidité et améliorer le confort et la santé de l\'habitat.' },
  { icon: DoorOpen, title: 'Menuiseries', desc: 'Remplacement de fenêtres et portes par des menuiseries isolantes pour supprimer les déperditions et le bruit.' },
  { icon: Home, title: 'Rénovation globale', desc: 'Bouquet de travaux coordonnés (isolation + ventilation + menuiseries) pour une amélioration cohérente et durable.' },
];

const benefits = [
  'Confort thermique été comme hiver',
  'Réduction des consommations d\'énergie',
  'Amélioration du DPE et valorisation du bien',
  'Matériaux étudiés, adaptés au bâti ancien et récent',
];

const RenovationEnergetique = () => {
  return (
    <main className="pt-28" data-testid="page-renovation-energetique">
      <Seo
        title="Rénovation énergétique & isolation biosourcée à Paris | VIREO"
        description="Rénovation énergétique à Paris et en Île-de-France : isolation (y compris biosourcée : fibre de bois, chanvre, liège), ventilation et menuiseries pour améliorer le confort et le DPE. Devis gratuit."
        path="/renovation-energetique"
        jsonLd={breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Rénovation énergétique', path: '/renovation-energetique' }])}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1543525324-dc2c9006d03d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Isolation thermique en rénovation"
            className="w-full h-full object-cover opacity-25"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full px-4 py-2 text-emerald-200 text-sm font-medium mb-6">
              <Gauge className="h-4 w-4" /> Rénovation énergétique
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Améliorez le confort et la performance de votre logement
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-8">
              Isolation, ventilation et menuiseries : VIREO conçoit des solutions de rénovation
              énergétique adaptées à votre bien, à Paris et en Île-de-France.
            </p>
            <Link
              to="/#contact"
              data-testid="energy-hero-cta"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:gap-3 shadow-xl"
            >
              Demander un devis gratuit <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Nos solutions</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Nous traitons les postes clés de la performance énergétique, seuls ou en bouquet de travaux
              coordonnés pour un résultat cohérent et durable.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} data-testid={`energy-solution-${i}`} className="border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all duration-300 bg-white group">
                  <CardContent className="p-7 flex gap-5">
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors duration-300">
                      <Icon className="h-7 w-7 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{s.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{s.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-emerald-700 font-medium mb-4">
                <Leaf className="h-5 w-5" /> Des bénéfices concrets
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Pourquoi engager une rénovation énergétique ?
              </h2>
              <ul className="space-y-4">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-slate-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl h-80 lg:h-96">
              <img
                src="https://images.unsplash.com/photo-1593786267440-550458cc882a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Chantier de rénovation énergétique"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-10 md:p-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Prêt à améliorer votre confort ?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Décrivez-nous votre projet et joignez vos plans ou photos : nous établissons une étude gratuite.
            </p>
            <Link to="/#contact">
              <Button className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-8 py-6 text-lg" data-testid="energy-bottom-cta">
                Obtenir mon devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RenovationEnergetique;
